import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LinkProps } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { SpinnerIcon } from '@/components/icons'

/**
 * 按钮。
 *
 * 变体表是「一条完整类名串」而不是拼接片段：拼接会在 Tailwind 里产生难以预料的
 * 覆盖顺序（`bg-primary` 与 `bg-transparent` 谁赢取决于生成顺序而非书写顺序）。
 * 一条串一个变体，读起来也一眼能看出深浅两套主题分别长什么样。
 *
 * 颜色全部走语义 token（AGENTS.md 6：禁止行内颜色值），所以这里看不到 `dark:`
 * ——token 本身在 `.dark` 下已经换了值。只有 `primary` 这种「两套主题下前景色
 * 关系不同」的情况才需要显式处理。
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'navy'
export type ButtonSize = 'sm' | 'md' | 'lg'

const BASE =
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-card font-medium transition-all duration-150 ease-out-soft select-none disabled:pointer-events-none disabled:opacity-50'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-foreground shadow-card hover:bg-primary-hover active:translate-y-px',
  secondary:
    'border border-border bg-surface text-foreground hover:border-border-strong hover:bg-surface-muted active:translate-y-px',
  ghost: 'text-muted-foreground hover:bg-surface-muted hover:text-foreground',
  danger: 'bg-danger text-white hover:opacity-90 active:translate-y-px',
  navy: 'bg-navy text-navy-foreground hover:opacity-90 active:translate-y-px',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[0.8125rem]',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-[0.9375rem]',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** 撑满容器宽度。移动端的主操作按钮基本都要。 */
  block?: boolean
  /** 进行中：显示 spinner 并禁用，避免重复提交。 */
  loading?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    block = false,
    loading = false,
    leadingIcon,
    trailingIcon,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      // 读屏器需要知道「正在处理」，视觉上的 spinner 对它不可见。
      aria-busy={loading || undefined}
      className={cn(BASE, VARIANTS[variant], SIZES[size], block && 'w-full', className)}
      {...props}
    >
      {loading ? <SpinnerIcon className="size-4 animate-spin" /> : leadingIcon}
      {children}
      {trailingIcon}
    </button>
  )
})

/** 纯图标按钮。`label` 是必填的 —— 没有可见文字时读屏器只能靠它。 */
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  variant?: ButtonVariant
  size?: 'sm' | 'md' | 'lg'
}

const ICON_SIZES: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-11',
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', className, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        BASE,
        VARIANTS[variant],
        ICON_SIZES[size],
        'shrink-0 p-0 text-sm',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
})

/**
 * 长得像按钮的链接。
 *
 * 语义上是导航就该用 `<a>`：中键新开标签页、右键复制链接、读屏器把它读作
 * 链接而不是按钮 —— 这些都不是靠 `onClick={navigate}` 能补回来的。
 */
export interface ButtonLinkProps extends Omit<LinkProps, 'className'> {
  variant?: ButtonVariant
  size?: ButtonSize
  block?: boolean
  className?: string
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
}

export function ButtonLink({
  variant = 'primary',
  size = 'md',
  block = false,
  className,
  leadingIcon,
  trailingIcon,
  children,
  ...props
}: ButtonLinkProps) {
  return (
    <Link
      className={cn(BASE, VARIANTS[variant], SIZES[size], block && 'w-full', className)}
      {...props}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </Link>
  )
}
